import SlidingPanel from "@components/sliders/SlidingPanel";
import React, { useEffect, useState } from "react";

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  documentTitle: string;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  isOpen,
  onClose,
  documentUrl,
  documentTitle,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      const isPdf = documentUrl.toLowerCase().includes(".pdf");

      if (isPdf) {
        // For PDFs, open in new tab and close modal
        window.open(documentUrl, "_blank");
        onClose();
      } else {
        setIsLoading(true);
        setHasError(false);
      }
    }
  }, [isOpen, documentUrl, onClose]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const isImageFile = (url: string) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    return imageExtensions.some((ext) => url.toLowerCase().includes(ext));
  };

  const renderDocumentContent = () => {
    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <span className="material-symbols-outlined text-6xl text-text-subtle mb-4">
            error_outline
          </span>
          <p className="text-text-base text-lg font-medium mb-2">
            Unable to load image
          </p>
          <p className="text-text-subtle text-sm mb-4">
            The image could not be displayed.
          </p>
          <button
            onClick={() => window.open(documentUrl, "_blank")}
            className="px-4 py-2 bg-bg-button-primary text-text-button-primary rounded-md hover:bg-bg-button-primary-hover transition-colors"
          >
            Open in New Tab
          </button>
        </div>
      );
    }

    // Only handle images in the modal
    return (
      <div className="flex items-center justify-center h-full p-4">
        <img
          src={documentUrl}
          alt={documentTitle}
          className="max-w-full max-h-full object-contain rounded-md shadow-lg"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
    );
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClose={onClose}
      heading={documentTitle}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="relative w-full h-full pt-16 pb-4">
        {/* pt-16 to account for fixed header */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-bg-page-body">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-border-button-primary mb-4"></div>
              <p className="text-text-subtle">Loading document...</p>
            </div>
          </div>
        )}
        {renderDocumentContent()}
      </div>
    </SlidingPanel>
  );
};

export default DocumentViewerModal;
